import {useState} from 'react';
import {NextPage} from 'next';
import {useRouter} from 'next/router';

import {useMarkActionPlanOnboardingComplete} from '#apollo/mutation/useMarkActionPlanOnboardingComplete';
import {useActionPlanPageQuery} from '#apollo/schema';
import {Screen, Text} from '#component/base';
import {ModalRoot} from '#component/base/Modal';
import {ActionPlanOnboarding} from '#component/modal/ActionPlanOnboarding';
import {ActionPlanPage} from '#component/page/ActionPlan/ActionPlan.types';
import {ActionPlanGoals, FullscreenLoading} from '#component/partial';
import {Transition} from '#component/util';
import {withRequireUserState} from '#component/util/RequireUserState/RequireUserState';
import {useViewportSize} from '#hook';
import * as DESTINATION from '#hook/useReturnUrl/defaultDestination';
import {sprinkles} from '#style/sprinkles';

import {Header} from './components';
import {Buttons} from './components/Buttons';
import {CheckInSection} from './components/CheckInSection';
import {Hero} from './components/Hero/Hero';

import * as css from './ActionPlan.css';

export const ActionPlanScreen = () => {
  const {isDesktop} = useViewportSize();
  const {data, error, loading} = useActionPlanPageQuery();
  const {me, currentTestPeriod, currentCheckIn} = data ?? {};
  const {
    userProfile,
    completedActionPlanOnboarding,
    mostRecentLabSubmission,
    mostRecentLabSubmissionWithResults,
    subscription,
  } = me ?? {};
  const hasMembership = subscription?.hasMembership;
  const router = useRouter();
  const surveySubmissionId = mostRecentLabSubmission?.userSurveySubmission?.id;
  const {deltaAge} = mostRecentLabSubmissionWithResults?.labAssessment ?? {};
  const {actionPlans} =
    mostRecentLabSubmissionWithResults?.userSurveySubmission?.surveyAssessment ?? {};
  const actionPlan = actionPlans?.[0];
  const canCheckIn = !!currentCheckIn && !currentCheckIn?.completed;

  if (!loading && !hasMembership) {
    router.replace(DESTINATION.HOME);
  }

  const {markActionPlanOnboardingComplete, updatingActionPlanOnboardingComplete} =
    useMarkActionPlanOnboardingComplete();

  const [onboardingOpened, setOnboardingOpened] = useState(
    !completedActionPlanOnboarding && !!actionPlans,
  );

  const handleCloseOnboarding = () => {
    setOnboardingOpened(false);
  };

  const handleSubmitOnboarding = () => {
    markActionPlanOnboardingComplete().then(() => {
      setOnboardingOpened(false);
    });
  };

  const handleOpenModal = () => {
    setOnboardingOpened(true);
  };

  const pageState: ActionPlanPage.State = error
    ? 'error'
    : !subscription?.hasActiveSubscription
    ? 'non-member'
    : !mostRecentLabSubmission?.id
    ? 'missing-lab'
    : !surveySubmissionId && !deltaAge
    ? 'missing-survey-and-lab'
    : !surveySubmissionId
    ? 'missing-survey'
    : !deltaAge && deltaAge !== 0
    ? 'missing-lab-result'
    : !actionPlan
    ? 'error'
    : actionPlan?.active
    ? 'active'
    : 'inactive';

  const isActionPlanActive = pageState === 'active';

  return hasMembership ? (
    <Screen
      loading={loading}
      headerProps={{variant: isActionPlanActive ? 'light' : 'dark'}}
      className={sprinkles({
        bg: 'ecruWhite',
      })}
      pageName="Action Plan"
      pageData={{
        hasSelectedActionPlan: isActionPlanActive,
      }}
      withDefaultSizing={false}>
      {!isActionPlanActive && <Hero pageState={pageState} onOpenModal={handleOpenModal} />}

      <div className={css.header({isActionPlanActive})}>
        <Header
          userFirstname={userProfile?.firstName}
          pageState={pageState}
          actionPlanId={actionPlan?.id}
          adjustments={actionPlan?.adjustments}
          deltaAge={deltaAge || 0}
          canCheckIn={canCheckIn}
        />
      </div>
      <div
        className={sprinkles({
          mB: 64,
        })}>
        {isActionPlanActive && (
          <div
            className={sprinkles({
              layout: 'flex',
              pT: 56,
              pB: 56,
            })}
            style={{
              borderTop: '1px solid #E5E5E5',
            }}>
            <Text as="h4" color="deepBurgundy">
              You’ve committed to the following goals:
            </Text>
          </div>
        )}

        <ActionPlanGoals actionPlan={actionPlan} surveySubmissionId={surveySubmissionId} />
        <Buttons
          buttonState={isDesktop || isActionPlanActive ? 'hidden' : pageState}
          actionPlanId={actionPlan?.id}
          isActionPlanActive={isActionPlanActive}
        />
      </div>
      <div
        className={sprinkles({
          pY: 96,
          w: 'full',
          bg: 'cotton',
        })}>
        <Transition appear show>
          <CheckInSection
            pageState={pageState}
            checkInStats={currentTestPeriod?.checkInStats || []}
            currentCheckIn={currentCheckIn}
          />
        </Transition>
      </div>
      <ModalRoot open={onboardingOpened}>
        <ActionPlanOnboarding
          onClose={handleCloseOnboarding}
          onSubmit={handleSubmitOnboarding}
          saving={updatingActionPlanOnboardingComplete}
          isOpen={onboardingOpened}
        />
      </ModalRoot>
    </Screen>
  ) : (
    <FullscreenLoading />
  );
};

export const ActionPlan: NextPage = withRequireUserState(ActionPlanScreen, {
  userState: 'verified',
});
